import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveHeader,
  ResponsiveFooter,
  Card,
} from '../ui';
import { API_BASE_URL } from '../../config/api';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { unifiedContactService } from '../../services/unifiedContactService';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { Button } from '../ui';
import { ENABLE_BENEFICIARY_PORTAL } from '../../config/planAccessConfig';

interface BeneficiaryDisplay {
    contactId: string;
    email: string;
    name: string;
    relationship?: string;
    // Only present when there's an invite record
    inviteId?: string;  // UUID from API
    status?: 'pending' | 'accepted' | 'revoked';
  }

interface ExecutorDisplay {
    contactId: string;
    email: string;
    name: string;
    relationship?: string;
    inviteId?: string;
    status?: 'pending' | 'accepted' | 'revoked';
  }

const EDIT_BUTTON_CLASS =
  'px-4 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-800 transition-colors text-sm font-medium';

const getStatusLabel = (status: string) =>
  status === 'pending' ? 'Invite sent' : status === 'accepted' ? 'Joined' : 'Not invited';

const Frame304: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [executors, setExecutors] = useState<ExecutorDisplay[]>([]);
  const [beneficiaries, setBeneficiaries] = useState<BeneficiaryDisplay[]>([]);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [executorCount, setExecutorCount] = useState(0);
  const [beneficiaryCount, setBeneficiaryCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { session, completeTask, updateTaskProgress } = useSessionPersistence();


  useEffect(() => {
    const loadData = async () => {
        setLoadError(null);
        try {
            const token = localStorage.getItem('account-token');
            const allContacts = await unifiedContactService.getAllContacts();

            let beneficiariesWithStatus: BeneficiaryDisplay[] = [];

            if (ENABLE_BENEFICIARY_PORTAL) {
              const responseBeneficiaries = await fetch(`${API_BASE_URL}/api/beneficiaries/invites`, {
                  headers: {
                      'Authorization': `Bearer ${token}`
                  }
              });
              if (!responseBeneficiaries.ok) {
                  throw new Error('Failed to load beneficiary invites');
              }
              const dataBeneficiaries = await responseBeneficiaries.json();
              const rawBeneficiaries = dataBeneficiaries.beneficiaries ?? [];

              const savedDistributions = session.taskProgress?.assetDistribution?.answers?.distributions || {};

              // Make sure savedDistributions is an array not an object
              const distList = Array.isArray(savedDistributions) ? savedDistributions : Object.values(savedDistributions || {});

              // Get all unique beneficiary IDs using set to avoid duplicates
              const beneficiaryIds = [...new Set(
                  distList.flatMap((d: unknown) => [
                      ...(d.primaryBeneficiaries || []).map((p: unknown) => p.beneficiaryId),
                      ...(d.contingentBeneficiaries || []).map((c: unknown) => c.beneficiaryId)
                  ].filter(Boolean)
                  )
              )];

              // Map all IDs to their corresponding contacts
              const allBeneficiariesFromAssets = beneficiaryIds
                  .map(id => allContacts.find(c => c.id === id))
                  .filter((c): c is UnifiedContact => c != null);
              

              // Build invite lookup: use most recent invite per email (API returns ORDER BY created_at DESC)
              const inviteByEmail = new Map<string, { inviteId: string; status: string; email?: string }>();
              for (const inv of rawBeneficiaries || []) {
                  const key = inv.email?.toLowerCase();
                  if (key && !inviteByEmail.has(key)) {
                      inviteByEmail.set(key, inv);
                  }
              }

              // Merge: all asset-assigned contacts + invite status
              beneficiariesWithStatus = allBeneficiariesFromAssets.map(contact => {
                  const invite = inviteByEmail.get(contact.email?.toLowerCase()) as { inviteId: string; status: string } | undefined; // Find the most recent invite for this contact
                  const result: BeneficiaryDisplay = {
                    contactId: contact.id,
                    email: contact.email,
                    name: `${contact.firstName} ${contact.lastName}`.trim(),
                    relationship: contact.relationship,
                  };
                  if (invite) {
                    result.inviteId = invite.inviteId;
                    result.status = invite.status as 'pending' | 'accepted' | 'revoked';
                  }
                  return result;
                });
    
              setBeneficiaries(beneficiariesWithStatus);
              setBeneficiaryCount(beneficiariesWithStatus.filter(b => b.status && b.status !== 'revoked').length);
            } else {
              setBeneficiaries([]);
              setBeneficiaryCount(0);
            }



            const responseExecutors = await fetch(`${API_BASE_URL}/api/executors/invites`, {
                headers: {
                    'Authorization' : `Bearer ${token}`
                },
            });
            if (!responseExecutors.ok) {
                throw new Error('Failed to load executor invites');
            }
            const dataExecutors = await responseExecutors.json();
            const rawExecutors = dataExecutors.executors ?? [];

            // Source: selected executors from session (legal_essentials)
            // Session stores full contact data for selected executors so there's no need to use contacts to look up relationship
            const selectedExecutors = session.taskProgress?.executors?.answers?.selectedExecutors || [];
            const allExecutorsFromSession: ExecutorDisplay[] = selectedExecutors.map((e: { id: string; firstName: string; lastName: string; email: string }) => ({
              contactId: e.id,
              email: e.email,
              name: `${e.firstName} ${e.lastName}`.trim(),
              relationship: allContacts.find((c) => c.id === e.id)?.relationship,
            }));

            // Build invite lookup: most recent invite per email (API returns ORDER BY created_at DESC)
            const executorInviteByEmail = new Map<string, { inviteId: string; status: string; email?: string; name?: string }>();
            for (const inv of rawExecutors) {
                const key = inv.email?.toLowerCase();
                if (key && !executorInviteByEmail.has(key)) {
                    executorInviteByEmail.set(key, inv);
                }
            }

            // Merge: selected executors + invite status (like beneficiaries)
            const executorsWithStatus: ExecutorDisplay[] = allExecutorsFromSession.map((exec) => {
                const invite = executorInviteByEmail.get(exec.email?.toLowerCase()) as { inviteId: string; status: string } | undefined;
                const result: ExecutorDisplay = { ...exec };
                if (invite) {
                    result.inviteId = invite.inviteId;
                    result.status = invite.status as 'pending' | 'accepted' | 'revoked';
                }
                return result;
            });

            // Add API-only invites (e.g. from Frame261) not already in selection
            const seenExecutorEmails = new Set(executorsWithStatus.map((e) => e.email?.toLowerCase()).filter(Boolean));
            for (const inv of rawExecutors) {
                const key = inv.email?.toLowerCase();
                if (key && !seenExecutorEmails.has(key)) {
                    seenExecutorEmails.add(key);
                    executorsWithStatus.push({
                        contactId: inv.inviteId ?? '',
                        email: inv.email ?? '',
                        name: inv.name ?? '',
                        inviteId: inv.inviteId,
                        status: inv.status as 'pending' | 'accepted' | 'revoked',
                    });
                }
            }

            setExecutors(executorsWithStatus);
            setExecutorCount(executorsWithStatus.filter((e) => e.status && e.status !== 'revoked').length);
            const executorPending = executorsWithStatus.filter((e) => e.status === 'pending').length;
            const executorAccepted = executorsWithStatus.filter((e) => e.status === 'accepted').length;
            if (ENABLE_BENEFICIARY_PORTAL) {
              setPendingCount(beneficiariesWithStatus.filter(b => b.status === 'pending').length + executorPending);
              setAcceptedCount(beneficiariesWithStatus.filter(b => b.status === 'accepted').length + executorAccepted);
            } else {
              setPendingCount(executorPending);
              setAcceptedCount(executorAccepted);
            }
        } catch (error) {
            console.error('[Frame304] Error loading data:', error);
            setLoadError('Failed to load plan access data. Please refresh the page.');
        }
    };
    loadData();
  }, [session, location.pathname]);

  const handleRevokeAccessBeneficiary = async (inviteId: string) => {
    try{
        const token = localStorage.getItem('account-token');
        const response = await fetch(`${API_BASE_URL}/api/beneficiaries/revoke-invite`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ inviteId })
        });
        if (!response.ok) {
            throw new Error('Failed to revoke access');
        }

        const beneficiary = beneficiaries.find((b) => b.inviteId === inviteId);
        if (beneficiary?.status === 'pending') setPendingCount((p) => p - 1);
        if (beneficiary?.status === 'accepted') setAcceptedCount((a) => a - 1);

        setBeneficiaries(beneficiaries.map((b) => 
            b.inviteId === inviteId 
        ? { ...b, status: 'revoked' as const }
        : b
        ));
        setBeneficiaryCount((c) => c - 1);
        
        // Remove revoked beneficiary from session so Frame303 won't show them as selected
        const currentBeneficiaries = session.taskProgress?.invite_beneficiaries?.answers?.selectedBeneficiaries || [];
        const beneficiaryEmail = beneficiary?.email?.toLowerCase();

        if(beneficiaryEmail){
            const filtered = currentBeneficiaries.filter((e: {email?: string}) => (e.email?.toLowerCase() ?? '') !== beneficiaryEmail);
            await updateTaskProgress('invite_beneficiaries', { selectedBeneficiaries: filtered })
        }
    } catch (error) {
        console.error('[Frame304] Error revoking access:', error);
    }
   
  }

const handleResendInviteBeneficiary = async (inviteId: string) => {
    try{
        const token = localStorage.getItem('account-token');
        const response = await fetch(`${API_BASE_URL}/api/beneficiaries/resend-invite`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ inviteId })
        });
        if (!response.ok) {
            throw new Error('Failed to resend invite');
        }
        const data = await response.json();
        console.log('[Frame304] Invite resent:', data);
    } catch (error) {
        console.error('[Frame304] Error resending invite:', error);
    }
  };

  const handleResendInviteExecutor = async (inviteId: string) => {
    try{
        const token = localStorage.getItem('account-token');
        const response = await fetch(`${API_BASE_URL}/api/executors/resend-invite`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ inviteId })
        });
        if (!response.ok) {
            throw new Error('Failed to resend invite');
        }
        const data = await response.json();
        console.log('[Frame304] Invite resent:', data);
    } catch (error) {
        console.error('[Frame304] Error resending invite:', error);
    }
  };

  const handleRevokeAccessExecutor = async (inviteId: string) => {
    try{
        const token = localStorage.getItem('account-token');
        const response = await fetch(`${API_BASE_URL}/api/executors/revoke-invite`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ inviteId })
        });
        if (!response.ok) {
            throw new Error('Failed to revoke access');
        }

        const executor = executors.find((e) => e.inviteId === inviteId);
        if (executor?.status === 'pending') setPendingCount((p) => p - 1);
        if (executor?.status === 'accepted') setAcceptedCount((a) => a - 1);

        setExecutors(executors.map((e) => 
            e.inviteId === inviteId 
        ? { ...e, status: 'revoked' as const } 
        : e
        ));
        setExecutorCount((c) => c - 1);

        // Remove revoked executor from session so Frame302 won't show them as selected
        // Match by email
        const currentExecutors = session.taskProgress?.executors?.answers?.selectedExecutors || [];
        const executorEmail = executor?.email?.toLowerCase();
        if (executorEmail) {
            const filtered = currentExecutors.filter((e: { email?: string }) => (e.email?.toLowerCase() ?? '') !== executorEmail);
            await updateTaskProgress('executors', { selectedExecutors: filtered });
        }
    } catch (error) {
        console.error('[Frame304] Error revoking access:', error);
    }
  };


  const handleNext = async () => {
    completeTask('confirm_plan_access');
    navigate('/frame-140');
  }

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Manage Plan Access" />

        <div className="max-w-5xl space-y-6">
          <p className="text-stone-800 text-lg font-medium font-sans mb-8">
          {ENABLE_BENEFICIARY_PORTAL
            ? 'Control who can view your plan or help carry out your wishes. You can invite executors and beneficiaries and update access anytime.'
            : 'Control who can view your plan or help carry out your wishes. You can invite executors and manage access anytime.'}
          </p>

          {loadError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {loadError}
            </div>
          )}

          {/* Overview Section */}
          <Card className="mb-6">
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Plan Access Overview</h3>
            <div className={`grid grid-cols-1 gap-4 ${ENABLE_BENEFICIARY_PORTAL ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <div className="text-2xl font-bold text-cyan-900">{executorCount}</div>
                <div className="text-sm text-stone-600">Executors invited</div>
              </div>
              {ENABLE_BENEFICIARY_PORTAL && (
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <div className="text-2xl font-bold text-cyan-900">{beneficiaryCount}</div>
                <div className="text-sm text-stone-600">Beneficiaries invited</div>
              </div>
              )}
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <div className="text-2xl font-bold text-cyan-900">{pendingCount}</div>
                <div className="text-sm text-stone-600">Pending invites</div>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <div className="text-2xl font-bold text-cyan-900">{acceptedCount}</div>
                <div className="text-sm text-stone-600">Joined users</div>
              </div>
            </div>
          </Card>

          {/* Executor Access Section */}
          <Card className="overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Executor Access</h3>
              <button onClick={() => navigate('/frame-302')} className={EDIT_BUTTON_CLASS}>
                Edit
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-3">
              {executors.length === 0 ? (
                <div className="text-center py-8 px-4">
                    <p className="text-base font-semibold text-stone-800 mb-2">No executor added yet.</p>
                    <p className="text-sm text-stone-600 leading-relaxed max-w-md mx-auto mb-5">Your executor is the person responsible for carrying out your wishes and managing your estate when the time comes.</p>
                    <Button variant="outline" onClick={() => navigate('/frame-302')}>
                      Add Executor
                    </Button>
                </div>
              ) : (
                executors.map((executor) => (
                  <div
                    key={executor.inviteId ?? executor.contactId ?? executor.email}
                    className="bg-stone-50 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3"
                  >
                    <div>
                      <div className="font-medium text-stone-800">{executor.name}</div>
                      <div className="text-sm text-stone-600">{executor.email}</div>
                      {executor.relationship && (
                        <div className="text-xs text-stone-500 capitalize mt-0.5">
                          {executor.relationship}
                        </div>
                      )}
                      <span className="inline-block mt-1 text-xs font-medium text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded">
                        {getStatusLabel(executor.status ?? 'not invited')}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {executor.inviteId && executor.status === 'pending' && (
                        <button
                          type="button"
                          className="text-sm text-cyan-700 hover:text-cyan-800 font-medium"
                          onClick={() => handleResendInviteExecutor(executor.inviteId!)}
                        >
                          Resend Invite
                        </button>
                      )}
                      {executor.inviteId && (executor.status === 'pending' || executor.status === 'accepted') && (
                        <button
                          type="button"
                          className="text-sm text-red-600 hover:text-red-700 font-medium"
                          onClick={() => handleRevokeAccessExecutor(executor.inviteId!)}
                        >
                          Revoke Access
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {ENABLE_BENEFICIARY_PORTAL && (
          <Card className="overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Beneficiary Access</h3>
              <button onClick={() => navigate('/frame-303')} className={EDIT_BUTTON_CLASS}>
                Edit
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-3">
              {beneficiaries.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <p className="text-base font-semibold text-stone-800 mb-2">No beneficiaries invited yet.</p>
                  <p className="text-sm text-stone-600 leading-relaxed max-w-md mx-auto mb-5">Beneficiaries can view limited planning information so they understand your intentions and avoid surprises later.</p>
                  <Button variant="outline" onClick={() => navigate('/frame-303')}>
                    Add Beneficiary
                  </Button>
                </div>
              ) : (
                beneficiaries.map((beneficiary) => (
                  <div
                    key={beneficiary.inviteId ?? beneficiary.contactId}
                    className="bg-stone-50 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3"
                  >
                    <div>
                      <div className="font-medium text-stone-800">{beneficiary.name}</div>
                      <div className="text-sm text-stone-600">{beneficiary.email}</div>
                      {beneficiary.relationship && (
                        <div className="text-xs text-stone-500 capitalize mt-0.5">
                          {beneficiary.relationship}
                        </div>
                      )}
                      <span className="inline-block mt-1 text-xs font-medium text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded">
                        {getStatusLabel(beneficiary.status ?? 'not invited')}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {beneficiary.status && beneficiary.status === 'pending' && (
                        <button
                          type="button"
                          className="text-sm text-cyan-700 hover:text-cyan-800 font-medium"
                          onClick={() => handleResendInviteBeneficiary(beneficiary.inviteId!)}
                        >
                          Resend Invite
                        </button>
                      )}
                      {(beneficiary.status && (beneficiary.status === 'pending' || beneficiary.status === 'accepted')) && (
                        <button
                          type="button"
                          className="text-sm text-red-600 hover:text-red-700 font-medium"
                          onClick={() => handleRevokeAccessBeneficiary(beneficiary.inviteId!)}
                        >
                          Revoke Access
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
          )}
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Confirm Plan Access"
        subtitle="Review and update access as needed"
        progressPercent={100}
        onBack={() => navigate(ENABLE_BENEFICIARY_PORTAL ? '/frame-303' : '/frame-302')}
        onNext={() => handleNext()}
        nextText="Continue to Dashboard"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame304;
